import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { AccessibilityToolbar } from "@/components/AccessibilityToolbar";
import "@/App.css";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-bg min-h-screen flex flex-col items-center justify-center px-4 py-10">
      <div className="fixed top-4 right-4 z-20">
        <AccessibilityToolbar triggerClassName="bg-white/70 border border-[#E6EDF2]" />
      </div>
      <div className="w-full max-w-md mmm-fade-up">
        <div className="flex flex-col items-center mb-6 text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_c36fe051-3d1f-49c1-a3e2-d610d9af7ab4/artifacts/fhd5bl7q_MovingMyMama-Logo-Web.png"
            alt="MovingMyMama" className="h-24 w-24 object-contain mb-3"
          />
          <h1 className="font-display text-2xl font-bold text-[#1C4358]">MovingMyMama</h1>
          <p className="text-sm text-[#4B5563]">Command Center · Senior Transitions CRM</p>
        </div>

        <Card className="p-6 sm:p-7 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-md)" }}>
          <h2 className="font-display text-lg font-semibold text-[#252221] mb-1">Welcome back</h2>
          <p className="text-sm text-muted-foreground mb-5">Sign in to your private workspace.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="login-email" className="mb-1.5 block text-[13px] font-medium">Email</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  aria-label="Email" autoComplete="email"
                  placeholder="you@movingmymama.com" className="pl-9 h-11" data-testid="login-email-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="login-password" className="mb-1.5 block text-[13px] font-medium">Password</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  aria-label="Password" autoComplete="current-password"
                  placeholder="••••••••" className="pl-9 h-11" data-testid="login-password-input" />
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="login-form-submit-button"
              className="w-full h-11 text-white text-base" style={{ backgroundColor: "var(--mmm-red)" }}>
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign in"}
            </Button>
            <div className="text-center">
              <Link to="/forgot-password" className="text-sm font-medium text-[#1C4358] hover:underline" data-testid="forgot-password-link">
                Forgot your password?
              </Link>
            </div>
          </form>
        </Card>
        <p className="text-center text-xs text-muted-foreground mt-5">
          Your Trusted Resource For Senior Transitions
        </p>
      </div>
    </div>
  );
}
